import {
  useAppKit,
  useAppKitAccount,
  useDisconnect,
} from "@reown/appkit/react";
// Ensure AppKit is created before using the hook
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSignMessage, useSwitchChain } from "wagmi";

import { OspToast } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/useConnectWallet";

const I18n = I18nUtil.createScoped(Translations);

// CAIP地址解析结果类型定义
interface CaipAddressParsed {
  chainNamespace?: string;
  chainId?: string;
  address?: string;
}

/**
 * 解析 CAIP 地址格式 (namespace:chainId:address)
 * @param caipAddress CAIP格式的地址字符串
 * @returns 解析后的对象，包含 chainNamespace, chainId, address
 */
const parseCaipAddress = (
  caipAddress: string | undefined,
): CaipAddressParsed => {
  if (!caipAddress || typeof caipAddress !== "string") {
    return {};
  }

  try {
    // CAIP格式: namespace:chainId:address
    const match = caipAddress.match(/^([^:]+):([^:]+):(.+)$/);

    if (!match) {
      log.warn("[文案]Invalid CAIP address format", { caipAddress });
      return {};
    }

    const [, chainNamespace, chainId, address] = match;

    return {
      chainNamespace,
      chainId,
      address,
    };
  } catch (error) {
    log.error("[文案]Failed to parse CAIP address", { caipAddress, error });
    return {};
  }
};

export const useConnect = () => {
  const { open } = useAppKit();
  const [loading, setLoading] = useState(false);
  const { disconnect } = useDisconnect();
  const connectRef = useRef(false);
  const { switchChainAsync, error: switchChainError } = useSwitchChain();
  const { address, isConnected, caipAddress, status } = useAppKitAccount();
  const isConnecting = status === "connecting";
  const { error: signError } = useSignMessage();

  // 使用 useMemo 优化 CAIP 地址解析性能
  const parsedCaipAddress = useMemo(() => {
    return parseCaipAddress(caipAddress);
  }, [caipAddress]);

  // 首先定义 onDisconnect
  const onDisconnect = useCallback(async () => {
    log.info("[文案]disconnect", { address, connectRef: connectRef.current });
    try {
      await disconnect?.();
      connectRef.current = false;
    } catch (error) {
      log.error("[文案]disconnect error", error);
    }
    // connectRef 是 ref，不需要加入依赖数组
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnect]);

  const onConnect = useCallback(async () => {
    try {
      const res = await open?.();
      connectRef.current = true;
      log.debug("[文案]onConnect", res);
    } catch (error) {
      log.error("[文案]onConnect error", error);
    }
  }, [open]);

  const onSwitchChain = useCallback(
    async (chainId: number) => {
      try {
        const res = await switchChainAsync({ chainId: chainId });
        log.debug("[文案]switchChainAsync", res, chainId);
        return true;
      } catch (error) {
        log.error("[文案]switchChainAsync error", error);
        OspToast.error({
          content: I18n.t("Switch chain failed, please try again"),
        });
        return false;
      }
    },
    [switchChainAsync],
  );

  useEffect(() => {
    if (!!switchChainError) {
      log.info("[文案]switchChain error", { switchChainError });
    }
  }, [switchChainError]);

  useEffect(() => {
    if (!!signError) {
      log.info("[文案]sign error", { signError });
      onDisconnect();
      setLoading(false);
    }
  }, [signError, onDisconnect]);

  return {
    onConnect,
    address,
    isConnecting,
    onDisconnect,
    isConnected,
    loading,
    connectRef,
    chainId: parsedCaipAddress.chainId,
    onSwitchChain,
    switchChainError,
    // 新增：解析后的 CAIP 地址信息
    caipAddress: {
      raw: caipAddress,
      parsed: parsedCaipAddress,
      chainNamespace: parsedCaipAddress.chainNamespace,
      chainId: parsedCaipAddress.chainId,
      address: parsedCaipAddress.address,
    },
  };
};
